import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/adminApi";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Plus, Pencil, Trash2, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

const empty = { id: "", title: "", category: "General", required_skills: [], preferred_skills: [], keywords: [] };
const toCsv = (a) => (a || []).join(", ");
const fromCsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

const AdminRoles = ({ onAuthError }) => {
  const [roles, setRoles] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmId, setConfirmId] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => adminApi.get("/admin/roles").then((r) => setRoles(r.data)).catch(onAuthError);
  useEffect(() => { load(); }, []);

  const parseBulk = (text) => {
    const t = text.trim();
    if (!t) return [];
    if (t.startsWith("[") || t.startsWith("{")) {
      const parsed = JSON.parse(t);
      return Array.isArray(parsed) ? parsed : [parsed];
    }
    // CSV: header row title,category,required_skills,preferred_skills,keywords (skills separated by ;)
    const lines = t.split("\n").filter((l) => l.trim());
    const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
    return lines.slice(1).map((line) => {
      const cells = line.split(",");
      const row = {};
      header.forEach((h, i) => { row[h] = (cells[i] || "").trim(); });
      ["required_skills", "preferred_skills", "keywords"].forEach((k) => {
        if (row[k]) row[k] = row[k].split(";").map((s) => s.trim()).filter(Boolean);
      });
      return row;
    });
  };

  const runBulk = async () => {
    let parsed;
    try { parsed = parseBulk(bulkText); }
    catch (e) { toast.error("Couldn't parse — check JSON/CSV format"); return; }
    if (!parsed.length) { toast.error("Nothing to import"); return; }
    setBulkBusy(true);
    try {
      const { data } = await adminApi.post("/admin/roles/bulk", { roles: parsed });
      toast.success(`Imported: ${data.created} new, ${data.updated} updated${data.skipped?.length ? `, ${data.skipped.length} skipped` : ""}`);
      setBulkOpen(false); setBulkText(""); load();
    } catch (e) { onAuthError(e); toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBulkBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (f) setBulkText(await f.text());
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => { setEditing(r.id); setForm({ ...r }); setOpen(true); };

  const save = async () => {
    const body = { ...form, required_skills: Array.isArray(form.required_skills) ? form.required_skills : fromCsv(form.required_skills),
      preferred_skills: Array.isArray(form.preferred_skills) ? form.preferred_skills : fromCsv(form.preferred_skills),
      keywords: Array.isArray(form.keywords) ? form.keywords : fromCsv(form.keywords) };
    if (!body.title) { toast.error("Title is required"); return; }
    try {
      if (editing) await adminApi.put(`/admin/roles/${editing}`, body);
      else await adminApi.post("/admin/roles", body);
      toast.success(editing ? "Role updated" : "Role added");
      setOpen(false); load();
    } catch (e) { onAuthError(e); toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (r) => {
    try { await adminApi.delete(`/admin/roles/${r.id}`); toast.success("Role deleted"); setConfirmId(""); load(); }
    catch (e) { onAuthError(e); toast.error("Delete failed"); }
  };

  const Field = ({ label, k }) => (
    <div className="mb-3">
      <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      <Input value={Array.isArray(form[k]) ? toCsv(form[k]) : (form[k] || "")} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="bg-slate-50 border-slate-300 text-slate-900" data-testid={`role-field-${k}`} />
    </div>
  );

  return (
    <div data-testid="admin-roles">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">{roles ? `${roles.length} roles` : ""} — these power Top Roles & Skill Gap matching.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulkOpen(true)} className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="admin-bulk-import"><Upload className="w-4 h-4 mr-1" /> Bulk import</Button>
          <Button onClick={openNew} className="bg-sky-600 hover:bg-sky-500 text-white" data-testid="admin-add-role"><Plus className="w-4 h-4 mr-1" /> Add role</Button>
        </div>
      </div>
      {!roles ? <div className="flex justify-center py-20 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {roles.map((r) => (
            <div key={r.id} className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-4" data-testid={`admin-role-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div><h4 className="font-bold text-slate-900">{r.title}</h4><p className="text-xs text-slate-500">{r.category}</p></div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(r)} className="h-7 w-7 text-slate-500 hover:text-slate-900" data-testid={`admin-role-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  {confirmId === r.id ? (
                    <Button size="sm" onClick={() => remove(r)} className="h-7 bg-rose-600 hover:bg-rose-500 text-white" data-testid={`admin-role-confirm-${r.id}`}>Sure?</Button>
                  ) : (
                    <Button size="icon" variant="ghost" onClick={() => setConfirmId(r.id)} className="h-7 w-7 text-rose-600 hover:text-rose-600" data-testid={`admin-role-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2 line-clamp-2"><span className="text-slate-500">Req:</span> {toCsv(r.required_skills) || "—"}</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit role" : "Add role"}</DialogTitle></DialogHeader>
          <Field label="Title" k="title" />
          <Field label="Category" k="category" />
          <Field label="Required skills (comma-separated)" k="required_skills" />
          <Field label="Preferred skills (comma-separated)" k="preferred_skills" />
          <Field label="Keywords (comma-separated)" k="keywords" />
          <Button onClick={save} className="w-full bg-sky-600 hover:bg-sky-500 text-white mt-2" data-testid="admin-role-save">{editing ? "Save changes" : "Create role"}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-xl">
          <DialogHeader><DialogTitle>Bulk import roles</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">
            Paste <span className="text-slate-800">JSON</span> (array of {"{title, category, required_skills, preferred_skills, keywords}"}) or
            <span className="text-slate-800"> CSV</span> (header row; skills separated by <span className="font-mono">;</span>). Existing ids are updated, new ones created.
          </p>
          <Textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={9} placeholder={'[{"title":"Data Engineer","category":"Data","required_skills":["sql","python"],"keywords":["etl"]}]'} className="bg-slate-50 border-slate-300 text-slate-900 font-mono text-xs" data-testid="bulk-import-textarea" />
          <div className="flex items-center justify-between gap-2 mt-2">
            <label className="text-xs text-sky-600 hover:text-sky-700 cursor-pointer inline-flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5" /> Load file
              <input type="file" accept=".json,.csv,.txt" className="hidden" onChange={onFile} data-testid="bulk-import-file" />
            </label>
            <Button onClick={runBulk} disabled={bulkBusy} className="bg-sky-600 hover:bg-sky-500 text-white" data-testid="bulk-import-submit">
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import roles"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRoles;
